import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
} from '@mui/material';
import {
  PictureAsPdf,
  Visibility,
  ExpandMore,
  ExpandLess,
  Close,
  Download,
  FilePresent,
} from '@mui/icons-material';
import PdfViewer from '../PdfViewer/PdfViewer';

interface DocumentData {
  id: string;
  object: string;
  for_id: string;
  for_type: string;
  type: string;
  mime: string;
  file_name: string;
  path_to_file: string;
  download_url: string;
  expires_in: null;
  meta: null;
  created_at: string;
  updated_at: string;
}

interface DocumentListProps {
  documents: DocumentData[];
  title?: string;
  showDocumentCount?: boolean;
  defaultExpanded?: boolean;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  title = "Documents",
  showDocumentCount = true,
  defaultExpanded = true,
}) => {
  const [expandedList, setExpandedList] = useState(defaultExpanded);
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  const handleShowPdf = (document: DocumentData) => {
    setSelectedDocument(document);
    setPdfDialogOpen(true);
  };

  const handleClosePdf = () => {
    setPdfDialogOpen(false);
    setSelectedDocument(null);
  };

  const formatFileSize = (sizeInBytes?: number) => {
    if (!sizeInBytes) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeInBytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType === 'application/pdf') {
      return <PictureAsPdf color="error" />;
    }
    return <FilePresent color="action" />;
  };

  if (!documents || documents.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No documents available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setExpandedList(!expandedList)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6">
                {title}
              </Typography>
              {showDocumentCount && (
                <Chip
                  label={`${documents.length} ${documents.length === 1 ? 'document' : 'documents'}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
            </Box>
            <IconButton size="small">
              {expandedList ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={expandedList}>
            <Box sx={{ mt: 2 }}>
              <List sx={{ p: 0 }}>
                {documents.map((document, index) => (
                  <React.Fragment key={document.id}>
                    <ListItem
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'grey.50',
                        '&:hover': {
                          bgcolor: 'grey.100',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {getFileIcon(document.mime)}
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" fontWeight={500}>
                            {document.file_name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Type: {document.type?.replace(/_/g, ' ')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Created: {formatDate(document.created_at)}
                            </Typography>
                            {document.mime && (
                              <Chip
                                label={document.mime}
                                size="small"
                                variant="outlined"
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        }
                      />
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Visibility />}
                          onClick={() => handleShowPdf(document)}
                          sx={{ minWidth: 100 }}
                        >
                          Show
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          component="a"
                          href={document.download_url}
                          download={document.file_name}
                          sx={{ minWidth: 100 }}
                        >
                          Download
                        </Button>
                      </Box>
                    </ListItem>
                    {index < documents.length - 1 && <Divider sx={{ my: 1 }} />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog
        open={pdfDialogOpen}
        onClose={handleClosePdf}
        maxWidth={false}
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
          },
        }}
      >
        <DialogActions sx={{ p: 1, justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {selectedDocument?.file_name}
          </Typography>
          <IconButton onClick={handleClosePdf}>
            <Close />
          </IconButton>
        </DialogActions>
        
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {selectedDocument && (
            <Box sx={{ flex: 1, height: '100%' }}>
              <PdfViewer
                documentData={selectedDocument}
                onBack={handleClosePdf}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentList;
